import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment.js';
import type { MapConfigResponse, VectorTileLayerConfig } from '../types/vector-tile-layer.js';

/** Fallback max zoom for layers whose config omits one. */
const DEFAULT_VECTOR_TILE_MAX_ZOOM = 14;

/**
 * Fetches the backend's map layer catalog and exposes the internal vector
 * tile layers that can be draped onto the terrain once it is active.
 */
@Injectable({ providedIn: 'root' })
export class MapLayersService {
  private http = inject(HttpClient);

  readonly vectorTileLayers = signal<VectorTileLayerConfig[]>([]);

  private loadPromise: Promise<VectorTileLayerConfig[]> | null = null;

  /**
   * Fetch the map config once and cache the result for subsequent calls.
   */
  async loadCatalog(): Promise<VectorTileLayerConfig[]> {
    if (this.vectorTileLayers().length > 0) {
      return this.vectorTileLayers();
    }
    if (!this.loadPromise) {
      this.loadPromise = this.fetchCatalog();
    }
    return this.loadPromise;
  }

  private async fetchCatalog(): Promise<VectorTileLayerConfig[]> {
    try {
      const tenant = new URLSearchParams(window.location.search).get('tenant') ?? '';
      const response = await firstValueFrom(
        this.http.get<MapConfigResponse>(`${environment.apiUrl}/map/config?tenant=${tenant}`),
      );

      const layers: VectorTileLayerConfig[] = [];
      for (const [id, raw] of Object.entries(response?.layers ?? {})) {
        if (raw.formatType !== 'VectorTile' || raw.source !== 'internal') continue;
        layers.push({
          id,
          uri: raw.uri,
          labelDe: raw.label_de ?? id,
          labelEn: raw.label_en ?? id,
          maxZoom: raw.maxZoom ?? DEFAULT_VECTOR_TILE_MAX_ZOOM,
          color: raw.defaultColor ?? [0, 0, 0],
        });
      }

      this.vectorTileLayers.set(layers);
      return layers;
    } catch (error) {
      console.error('Error loading map layer catalog:', error);
      this.loadPromise = null;
      return [];
    }
  }
}
